import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static minFundsCount = 10
  static unlistedFundUnitPrice = 150

  static values = {
    locked: Boolean,
    exactCount: Number,
    exactCountEnforced: Boolean,
  }

  static targets = [
    "modal",
    "tbody",
    "diff",
    "diffWrapper",
    "allCount",
    "addedCount",
    "removedCount",
    "unchangedCount",
    "chip",
    "sortIcon",
    "openButton",
    "submitButton",
    "statusMessage",
    "summaryCurrentCount",
    "summaryCurrentBilling",
    "summaryCurrentUnitBilling",
    "summaryNewCount",
    "summaryNewBilling",
    "summaryNewUnitBilling",
    "extraFundsWrapper",
    "extraFundsList",
    "extraFundsCount",
    "extraFundsBilling",
  ]

  connect() {
    this.activeFilter = "all"
    this.rows = []

    this.sortKey = "eos"
    this.sortDirection = "asc"

    this.refreshActionState()
  }

  handleCurrentBillingLoaded() {
    this.updatePricingSummary()
  }

  open(event) {
    if (!this.canOpenOrSubmit()) {
      event?.preventDefault()
      this.refreshActionState()
      return
    }

    this.build()
    this.modalTarget.classList.remove("hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  confirm() {
    if (!this.canOpenOrSubmit()) return

    const form = this.element.closest("form")
    if (form) form.requestSubmit()
  }

  applyFilter(event) {
    this.activeFilter = event.currentTarget.dataset.filter || "all"
    this.updateChipsUI()
    this.render()
  }

  sort(event) {
    const key = event.currentTarget.dataset.sortKey

    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"
    } else {
      this.sortKey = key
      this.sortDirection = "asc"
    }

    this.updateSortIcons()
    this.render()
  }

  refreshActionState() {
    const selectedCount = this.currentCount()
    const changed = this.selectionChanged()
    const meetsMinimumFundsCount =
      this.exactCountEnforcedValue || selectedCount >= this.constructor.minFundsCount
    const meetsExactCount =
      !this.exactCountEnforcedValue || selectedCount === this.exactCountValue
    const allowed = !this.lockedValue && meetsMinimumFundsCount && meetsExactCount && changed

    if (this.hasOpenButtonTarget) {
      this.openButtonTarget.disabled = !allowed
      this.openButtonTarget.setAttribute("aria-disabled", String(!allowed))
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !allowed
      this.submitButtonTarget.setAttribute("aria-disabled", String(!allowed))
    }

    if (this.hasStatusMessageTarget) {
      if (this.lockedValue) {
        this.statusMessageTarget.textContent = "Vous devez être administrateur du compte client pour modifier l’abonnement."
      } else if (!meetsExactCount) {
        this.statusMessageTarget.textContent = `Vous devez sélectionner exactement ${this.exactCountValue} fonds.`
      } else if (!meetsMinimumFundsCount) {
        this.statusMessageTarget.textContent = `Vous devez sélectionner au moins ${this.constructor.minFundsCount} fonds pour modifier l’abonnement.`
      } else if (!changed) {
        this.statusMessageTarget.textContent = "Aucune modification détectée dans la sélection actuelle."
      } else {
        this.statusMessageTarget.textContent = ""
      }
    }

    if (this.hasDiffWrapperTarget && this.hasDiffTarget) {
      const diff = this.currentCount() - this.initialCount()

      if (diff === 0) {
        this.diffWrapperTarget.classList.add("hidden")
        this.diffTarget.textContent = ""
      } else {
        this.diffWrapperTarget.classList.remove("hidden")
        this.diffTarget.textContent = diff > 0 ? `+${diff}` : `${diff}`
      }
    }
  }

  build() {
    const checkboxes = this.checkboxes()

    const rows = []
    for (const cb of checkboxes) {
      const was = cb.dataset.initialChecked === "true"
      const is = cb.checked

      let status = null
      if (was && is) status = "Inchangé"
      if (!was && is) status = "Ajouté"
      if (was && !is) status = "Supprimé"
      if (!status) continue

      rows.push({
        eos: cb.dataset.eosCategoryName || "Sans catégorie EOS",
        fund: cb.dataset.fundName || "",
        company: cb.dataset.companyName || "",
        status,
      })
    }

    this.rows = rows
    this.updateCounters()

    this.activeFilter = "all"
    this.sortKey = this.sortKey || "eos"
    this.sortDirection = this.sortDirection || "asc"

    this.updateChipsUI()
    this.updateSortIcons()
    this.render()
    this.updatePricingSummary()
    this.renderExtraFundsSummary()
    this.refreshActionState()
  }

  updateCounters() {
    const added = this.rows.filter((r) => r.status === "Ajouté").length
    const removed = this.rows.filter((r) => r.status === "Supprimé").length
    const unchanged = this.rows.filter((r) => r.status === "Inchangé").length

    this.addedCountTarget.textContent = String(added)
    this.removedCountTarget.textContent = String(removed)
    this.unchangedCountTarget.textContent = String(unchanged)
    this.allCountTarget.textContent = String(this.rows.length)
  }

  updateChipsUI() {
    this.chipTargets.forEach((chip) => {
      const isActive = (chip.dataset.filter || "all") === this.activeFilter
      chip.classList.toggle("ring-2", isActive)
      chip.classList.toggle("ring-oxford-900", isActive)
      chip.classList.toggle("ring-offset-1", isActive)
    })
  }

  applySorting(data) {
    const key = this.sortKey
    const dir = this.sortDirection

    return [...data].sort((a, b) => {
      const cmp = (a[key] || "").localeCompare(b[key] || "")
      if (cmp !== 0) return dir === "asc" ? cmp : -cmp

      const eosCmp = (a.eos || "").localeCompare(b.eos || "")
      if (eosCmp !== 0) return eosCmp

      const fundCmp = (a.fund || "").localeCompare(b.fund || "")
      if (fundCmp !== 0) return fundCmp

      return (a.company || "").localeCompare(b.company || "")
    })
  }

  updateSortIcons() {
    this.sortIconTargets.forEach((icon) => {
      const key = icon.dataset.sortIcon

      if (key === this.sortKey) {
        icon.style.transform =
          this.sortDirection === "asc" ? "rotate(180deg)" : "rotate(0deg)"
      } else {
        icon.style.transform = "rotate(0deg)"
      }
    })
  }

  render() {
    const filtered =
      this.activeFilter === "all"
        ? this.rows
        : this.rows.filter((r) => r.status === this.activeFilter)

    const sorted = this.applySorting(filtered)

    if (sorted.length === 0) {
      this.tbodyTarget.innerHTML = `
        <tr class="bg-white">
          <td colspan="4" class="px-3 py-6 text-center text-gray-500">
            Aucune ligne.
          </td>
        </tr>
      `
      return
    }

    this.tbodyTarget.innerHTML = sorted
      .map(
        (row, index) => `
      <tr class="border-t border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-pine-50/40"} hover:bg-pine-50 transition-colors">
        <td class="px-3 py-2 text-gray-700">${this.escape(row.eos)}</td>
        <td class="px-3 py-2 text-oxford-900 font-medium">${this.escape(row.fund)}</td>
        <td class="px-3 py-2">${this.statusBadge(row.status)}</td>
        <td class="px-3 py-2 text-right text-gray-700">${this.escape(row.company)}</td>
      </tr>
    `
      )
      .join("")
  }

  statusBadge(status) {
    let klass =
      "inline-flex items-center px-2 py-1 rounded-sm text-xs font-semibold border"
    if (status === "Ajouté") klass += " text-green-700 border-green-200 bg-green-50"
    if (status === "Supprimé") klass += " text-red-700 border-red-200 bg-red-50"
    if (status === "Inchangé") klass += " text-gray-700 border-gray-200 bg-gray-50"
    return `<span class="${klass}">${this.escape(status)}</span>`
  }

  checkboxes() {
    return Array.from(
      this.element.querySelectorAll(
        "input[type='checkbox'][name='subscription[fund_ids][]']"
      )
    )
  }

  initialCount() {
    return this.checkboxes().filter(
      (cb) => cb.dataset.initialChecked === "true"
    ).length
  }

  currentCount() {
    return this.checkboxes().filter((cb) => cb.checked).length + this.extraFundsCount()
  }

  selectionChanged() {
    return this.extraFundsCount() > 0 || this.checkboxes().some((cb) => {
      const was = cb.dataset.initialChecked === "true"
      return cb.checked !== was
    })
  }

  canOpenOrSubmit() {
    const meetsMinimumFundsCount =
      this.exactCountEnforcedValue || this.currentCount() >= this.constructor.minFundsCount
    const meetsExactCount =
      !this.exactCountEnforcedValue || this.currentCount() === this.exactCountValue

    return !this.lockedValue && meetsMinimumFundsCount && meetsExactCount && this.selectionChanged()
  }

  extraFundsCount() {
    return this.extraFunds().length
  }

  extraFunds() {
    return this.element.querySelectorAll(
      'input[type="hidden"][name="subscription[extra_fund_ids][]"]'
    )
  }

  updatePricingSummary() {
    if (this.hasSummaryCurrentCountTarget) {
      this.summaryCurrentCountTarget.textContent = String(this.initialCount())
    }

    if (this.hasSummaryNewCountTarget) {
      this.summaryNewCountTarget.textContent = String(this.currentCount())
    }

    if (this.hasSummaryCurrentBillingTarget) {
      this.renderCurrentBilling()
    }

    if (this.hasSummaryCurrentUnitBillingTarget) {
      this.renderCurrentUnitBilling()
    }

    if (this.hasSummaryNewBillingTarget) {
      this.summaryNewBillingTarget.textContent = this.formatBilling(
        this.newBillingAmount()
      )
    }

    if (this.hasSummaryNewUnitBillingTarget) {
      this.summaryNewUnitBillingTarget.textContent = this.formatUnitBilling(
        this.newBillingAmount(),
        this.currentCount()
      )
    }
  }

  currentBillingAmount() {
    const input = document.querySelector(
      '[data-sellsy-share-catalog-subscription-target="currentBillingInput"]'
    )

    if (!input) return null

    const amount = parseFloat(input.value)
    return Number.isFinite(amount) ? amount : null
  }

  newBillingAmount() {
    if (this.allFundsSelected()) return 9000

    const listedCount = this.checkboxes().filter((cb) => cb.checked).length
    const extraCount = this.extraFundsCount()

    if (listedCount <= 0 && extraCount <= 0) return 0

    let listedAmount = 0

    if (listedCount > 0) {
      if (listedCount <= 19) listedAmount = listedCount * 100
      else if (listedCount <= 29) listedAmount = listedCount * 90
      else if (listedCount <= 39) listedAmount = listedCount * 80
      else if (listedCount <= 49) listedAmount = listedCount * 73.75
      else listedAmount = listedCount * 68
    }

    return listedAmount + (extraCount * this.constructor.unlistedFundUnitPrice)
  }

  allFundsSelected() {
    const input = document.querySelector(
      "input[type='checkbox'][name='subscription[all_funds]']"
    )

    return !!input?.checked
  }

  formatBilling(amount) {
    if (amount === null || amount === undefined || Number.isNaN(amount)) {
      return "- € HT/an"
    }

    const formatted = new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount)

    return `${formatted} € HT/an`
  }

  renderCurrentBilling() {
    const amount = this.currentBillingAmount()

    if (amount === null) {
      this.summaryCurrentBillingTarget.innerHTML = this.loadingElement()
      return
    }

    this.summaryCurrentBillingTarget.textContent = this.formatBilling(amount)
  }

  renderCurrentUnitBilling() {
    const amount = this.currentBillingAmount()

    if (amount === null) {
      this.summaryCurrentUnitBillingTarget.textContent = "- € HT/fonds"
      return
    }

    this.summaryCurrentUnitBillingTarget.textContent = this.formatUnitBilling(
      amount,
      this.initialCount()
    )
  }

  loadingElement() {
    return `
      <span class="inline-flex items-center justify-center text-gray-500" aria-label="Chargement du coût actuel">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.25" stroke-width="3"></circle>
          <path d="M21 12a9 9 0 0 0-9-9" stroke="#2C685A" stroke-width="3" stroke-linecap="round">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 12 12"
              to="360 12 12"
              dur="0.8s"
              repeatCount="indefinite" />
          </path>
        </svg>
      </span>
    `
  }

  renderExtraFundsSummary() {
    if (!this.hasExtraFundsWrapperTarget || !this.hasExtraFundsListTarget) return

    const extraFunds = Array.from(this.extraFunds()).map((input) => ({
      name: input.dataset.fundName || "",
      company: input.dataset.fundCompany || "",
    }))

    if (extraFunds.length === 0) {
      this.extraFundsWrapperTarget.classList.add("hidden")
      this.extraFundsListTarget.innerHTML = ""
      return
    }

    this.extraFundsWrapperTarget.classList.remove("hidden")

    if (this.hasExtraFundsCountTarget) {
      this.extraFundsCountTarget.textContent = String(extraFunds.length)
    }

    if (this.hasExtraFundsBillingTarget) {
      this.extraFundsBillingTarget.textContent = this.formatBilling(
        extraFunds.length * this.constructor.unlistedFundUnitPrice
      )
    }

    this.extraFundsListTarget.innerHTML = extraFunds.map((fund) => `
      <li class="flex items-center justify-between gap-4 border-t border-gray-100 px-4 py-3 first:border-t-0">
        <div class="min-w-0">
          <div class="text-sm font-medium text-oxford-900">${this.escape(fund.name || "Fonds sans nom")}</div>
          <div class="text-xs text-gray-500">${this.escape(fund.company || "Société inconnue")}</div>
        </div>
        <span class="inline-flex items-center px-2 py-1 rounded-sm text-xs font-semibold border text-orange-800 border-orange-200 bg-orange-50 whitespace-nowrap">
          Soumis à examen
        </span>
      </li>
    `).join("")
  }

  formatUnitBilling(amount, count) {
    if (
      amount === null ||
      amount === undefined ||
      Number.isNaN(amount) ||
      !Number.isFinite(count) ||
      count <= 0
    ) {
      return "- € HT/fonds"
    }

    const unitAmount = amount / count
    const formatted = new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: Number.isInteger(unitAmount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(unitAmount)

    return `${formatted} € HT/fonds`
  }

  escape(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }
}
